import React from 'react'
import Image from 'next/image'

import type { FeatureCardsBlock, Media } from '@/payload-types'
import type { LinkFieldType } from '@/fields/link'
import { CMSLink } from '@/components/Link'
import { ArrowIcon } from '@/components/icons/ArrowIcon'
import { ArrowUpRightIcon } from '@/components/icons/ArrowUpRightIcon'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { cn } from '@/utilities/ui'

import { SpecList } from './SpecList'

export type FeatureCardStyle = NonNullable<FeatureCardsBlock['style']>
export type FeatureCardData = NonNullable<FeatureCardsBlock['cards']>[number]

interface FeatureCardProps {
  card: FeatureCardData
  style: FeatureCardStyle
}

type CmsLinkProps = Omit<LinkFieldType, 'appearance' | 'label'>

const hasUsableLink = (link: LinkFieldType | null | undefined): boolean => {
  if (!link) return false
  if (link.type === 'chat') return true
  if (link.type === 'jumpToBlock') return Boolean(link.anchorId)
  if (link.type === 'custom') return Boolean(link.url)
  return Boolean(link.reference)
}

const toCmsLinkProps = (link: LinkFieldType): CmsLinkProps => {
  const { appearance: _appearance, label: _label, ...rest } = link
  return rest
}

const resolveImage = (image: Media | string | null | undefined) => {
  if (!image) return null
  if (typeof image === 'string') return { url: image, alt: '' }
  const url = getMediaUrl(image.url, image.updatedAt)
  if (!url) return null
  return { url, alt: image.alt || '' }
}

const ARTICLE_CLASS = 'flex flex-col h-full'
const CARD_BODY_CLASS = 'flex flex-1 flex-col items-start w-full pt-6 pb-2 gap-2'
const FOCUS_RING_CLASS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-lime focus-visible:ring-offset-2'

interface CardFigureProps {
  src: string
  alt: string
  heightClass: string
  isInteractive: boolean
}

const CardFigure: React.FC<CardFigureProps> = ({ src, alt, heightClass, isInteractive }) => (
  <figure className={cn('relative w-full overflow-hidden rounded-[10px]', heightClass)}>
    <Image
      src={src}
      alt={alt}
      fill
      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 33vw, 25vw"
      className={cn(
        'object-cover transition-transform duration-500 ease-out',
        isInteractive && 'motion-safe:group-hover:scale-[1.04]',
      )}
    />

    {isInteractive && (
      <span
        aria-hidden="true"
        className={cn(
          'absolute top-6 right-6 flex items-center justify-center rounded-full bg-civo-lime size-12',
          'transition-all duration-300 ease-out opacity-0 scale-90',
          'motion-safe:group-hover:opacity-100 motion-safe:group-hover:scale-100',
          'motion-safe:group-focus-visible:opacity-100 motion-safe:group-focus-visible:scale-100',
        )}
      >
        <ArrowUpRightIcon stroke="black" />
      </span>
    )}
  </figure>
)

// Shared affordance (underline + typography + arrow) for both variations.
// Placed directly on the element that styles the underline so the border-b
// aligns cleanly with the text — nested inline spans can mis-align on some
// layouts when the card heights differ.
const LEARN_MORE_CLASS = cn(
  'inline-flex items-center pb-1 w-fit border-b border-black',
  'body-copy-medium font-medium text-black',
)

interface LearnMoreContentProps {
  label: string
}

const LearnMoreContent: React.FC<LearnMoreContentProps> = ({ label }) => (
  <>
    {label}
    <ArrowIcon className="ml-1 size-[14px] transition-transform duration-300 ease-out motion-safe:group-hover:translate-x-0.5" />
  </>
)

export const FeatureCard: React.FC<FeatureCardProps> = ({ card, style }) => {
  const { image, title, description, specs, learnMoreLabel, link } = card
  const resolved = resolveImage(image)
  const isInteractive = hasUsableLink(link)
  const showSpecs = style === 'withSpecs'
  const imageHeightClass =
    style === 'simple' ? 'h-[360px] lg:h-[470px]' : 'h-[280px] lg:h-[360px]'
  const imageAlt = resolved?.alt || title
  const linkProps = link ? toCmsLinkProps(link) : null

  const figureElement = resolved ? (
    <CardFigure
      src={resolved.url}
      alt={imageAlt}
      heightClass={imageHeightClass}
      isInteractive={isInteractive}
    />
  ) : null

  const titleElement = (
    <h3
      className={cn(
        'subheading-large text-black mb-0',
        isInteractive &&
          'motion-safe:group-hover:underline underline-offset-4 decoration-2',
      )}
    >
      {title}
    </h3>
  )

  const descriptionElement = description ? (
    <p className="body-copy-medium text-black/70 mb-0">{description}</p>
  ) : null

  const body = (
    <div className={CARD_BODY_CLASS}>
      {titleElement}
      {descriptionElement}
    </div>
  )

  // With Specs variation: split clickable regions so the description and spec
  // list stay inert. Image + title share one link; Learn more is a second link
  // to the same destination.
  if (showSpecs && isInteractive && linkProps) {
    return (
      <article className={cn(ARTICLE_CLASS, 'items-start')}>
        <CMSLink
          {...linkProps}
          appearance="inline"
          className={cn('group flex flex-1 flex-col w-full rounded-[10px]', FOCUS_RING_CLASS)}
        >
          {figureElement}
          {body}
        </CMSLink>

        {specs && specs.length > 0 && <SpecList specs={specs} />}

        <CMSLink
          {...linkProps}
          appearance="inline"
          className={cn('group mt-4', LEARN_MORE_CLASS, FOCUS_RING_CLASS)}
          aria-label={`${learnMoreLabel || 'Learn more'}: ${title}`}
        >
          <LearnMoreContent label={learnMoreLabel || 'Learn more'} />
        </CMSLink>
      </article>
    )
  }

  // Simple variation with a link: whole card is one clickable area.
  if (isInteractive && linkProps) {
    return (
      <article className="h-full">
        <CMSLink
          {...linkProps}
          appearance="inline"
          className={cn(
            'group flex flex-col h-full rounded-[20px] overflow-hidden',
            FOCUS_RING_CLASS,
          )}
        >
          {figureElement}
          {body}
          <div className="pb-6">
            <span className={LEARN_MORE_CLASS}>
              <LearnMoreContent label={learnMoreLabel || 'Learn more'} />
            </span>
          </div>
        </CMSLink>
      </article>
    )
  }

  // Non-interactive: no click targets, same vertical rhythm.
  return (
    <article className={cn(ARTICLE_CLASS, 'rounded-[20px] overflow-hidden')}>
      {figureElement}
      {body}
      {showSpecs && specs && specs.length > 0 && (
        <div className="pb-6 w-full">
          <SpecList specs={specs} />
        </div>
      )}
    </article>
  )
}
